/**
 * Per-customer payment dashboard.
 *
 * Three time windows side-by-side (today / this week / this month) with
 * total charged + transaction count + decline rate. Below: a category
 * breakdown bar chart for the active window. Powered by the existing
 * /api/payment-transactions list endpoint with date filters + summary.
 *
 * URL: /payment-dashboard
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { paymentTransactions } from '@/api/paymentTransactions';
import type { PaymentTransactionRow } from '@/api/paymentTransactions';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { CheckCircle2, XCircle, RotateCcw, Calendar, Download } from 'lucide-react';

type Window = 'today' | 'week' | 'month';

function windowDates(w: Window): { from: string; to: string; label: string } {
  const now = new Date();
  const to = now.toISOString();
  const from = new Date(now);
  if (w === 'today') {
    from.setUTCHours(0, 0, 0, 0);
    return { from: from.toISOString(), to, label: 'Today' };
  }
  if (w === 'week') {
    from.setUTCDate(from.getUTCDate() - 7);
    return { from: from.toISOString(), to, label: 'Last 7 days' };
  }
  // month
  from.setUTCDate(from.getUTCDate() - 30);
  return { from: from.toISOString(), to, label: 'Last 30 days' };
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function PaymentDashboardPage() {
  const customerId = useEffectiveCustomerId();
  const [activeWindow, setActiveWindow] = useState<Window>('today');

  if (!customerId) {
    return (
      <>
        <PageHeader
          eyebrow="Payments"
          title="Payment dashboard"
          description="Charge totals + category breakdown for your account."
        />
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground text-center">
            Select a customer first (Customers → Enter) to view the payment dashboard.
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Payments"
        title="Payment dashboard"
        description="Total charged + decline rate + category breakdown."
        actions={
          <Button
            variant="outline" size="sm"
            onClick={() => paymentTransactions.downloadCsv({
              customer_id: customerId,
              from: windowDates(activeWindow).from,
              to: windowDates(activeWindow).to,
              limit: 50000,
            }).catch(() => undefined)}
          >
            <Download className="h-4 w-4 me-1.5" />
            Export window CSV
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <WindowCard
          window="today"
          customerId={customerId}
          active={activeWindow === 'today'}
          onClick={() => setActiveWindow('today')}
        />
        <WindowCard
          window="week"
          customerId={customerId}
          active={activeWindow === 'week'}
          onClick={() => setActiveWindow('week')}
        />
        <WindowCard
          window="month"
          customerId={customerId}
          active={activeWindow === 'month'}
          onClick={() => setActiveWindow('month')}
        />
      </div>

      <CategoryBreakdown customerId={customerId} window={activeWindow} />
    </>
  );
}

function WindowCard({
  window: w, customerId, active, onClick,
}: {
  window: Window; customerId: string; active: boolean; onClick: () => void;
}) {
  const dates = windowDates(w);
  const { data } = useQuery({
    queryKey: ['payment-dashboard', customerId, w],
    queryFn: () => paymentTransactions.list({
      customer_id: customerId,
      from: dates.from,
      to: dates.to,
      limit: 1,    // we only need the summary
    }),
    staleTime: 30_000,
  });

  const summary = data?.summary;
  const declineRate = summary && summary.transactions_total > 0
    ? Math.round(summary.declined_count * 100 / summary.transactions_total)
    : 0;

  return (
    <Card
      onClick={onClick}
      className={cn(
        'cursor-pointer transition-all',
        active ? 'ring-2 ring-indigo-400 border-indigo-200' : 'hover:border-slate-300',
      )}
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5" />
          {dates.label}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="text-3xl font-semibold tabular-nums text-emerald-700">
          {summary ? formatMoney(summary.captured_cents) : '—'}
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1.5 text-emerald-700">
            <CheckCircle2 className="h-3 w-3" />
            <span className="font-mono tabular-nums">{summary?.captured_count ?? 0}</span> captured
          </div>
          <div className="flex items-center gap-1.5 text-red-600">
            <XCircle className="h-3 w-3" />
            <span className="font-mono tabular-nums">{summary?.declined_count ?? 0}</span> declined
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground">
          {summary?.transactions_total ?? 0} attempt{summary?.transactions_total === 1 ? '' : 's'}
          {summary && summary.transactions_total > 0 && (
            <> · <span className={declineRate > 20 ? 'text-amber-700 font-medium' : ''}>{declineRate}% decline</span></>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CategoryBreakdown({
  customerId, window: w,
}: { customerId: string; window: Window }) {
  const dates = windowDates(w);
  // Pull up to 1000 rows for the breakdown — enough for any reasonable
  // 30-day window. If needed, swap to a server-side aggregate query later.
  const { data, isLoading } = useQuery({
    queryKey: ['payment-dashboard-rows', customerId, w],
    queryFn: () => paymentTransactions.list({
      customer_id: customerId,
      from: dates.from,
      to: dates.to,
      status: 'captured',
      limit: 1000,
    }),
    staleTime: 30_000,
  });

  const categoryTotals = useMemo(() => {
    const map = new Map<string, number>();
    (data?.rows ?? []).forEach((r: PaymentTransactionRow) => {
      const key = r.category || 'uncategorized';
      map.set(key, (map.get(key) ?? 0) + r.amount_cents);
    });
    const arr = Array.from(map.entries()).map(([key, cents]) => ({ key, cents }));
    arr.sort((a, b) => b.cents - a.cents);
    return arr;
  }, [data]);

  const totalCents = categoryTotals.reduce((s, c) => s + c.cents, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">By category — {dates.label}</CardTitle>
        <CardDescription>
          Captured charges, sorted by total. "uncategorized" = nodes that
          don't tag a category.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : categoryTotals.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            No captured charges in this window.
          </p>
        ) : (
          <div className="space-y-2">
            {categoryTotals.map(({ key, cents }) => {
              const pct = totalCents > 0 ? Math.round(cents * 100 / totalCents) : 0;
              return (
                <div key={key}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{key}</span>
                    <span className="font-mono tabular-nums text-slate-600">
                      {formatMoney(cents)} <span className="text-xs text-slate-400">({pct}%)</span>
                    </span>
                  </div>
                  <div className="h-2 mt-1 rounded bg-slate-100 overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
            <div className="flex items-center justify-between pt-2 border-t border-border text-sm font-semibold">
              <span>Total</span>
              <span className="font-mono tabular-nums text-emerald-700">{formatMoney(totalCents)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Suppress lint-error: imported but unused in this file
void RotateCcw;
